import {
  DEFAULT_CONNECT_TIMEOUT,
  DEFAULT_MESSAGE_TIMEOUT,
  DEFAULT_UNAUTHORIZED_REDIRECT_URL,
} from './constants';
import type { WebSocketClientOptions } from './types';
import { WebSocketError } from './WebSocketError';

export class WebSocketClient {
  private readonly url: string;
  private readonly connectTimeout: number;
  private readonly messageTimeout: number;
  private readonly authOrigin: string;
  private readonly unauthorizedRedirectUrl: string;

  constructor(options: WebSocketClientOptions) {
    this.url = options.url;
    this.connectTimeout = options.connectTimeout ?? DEFAULT_CONNECT_TIMEOUT;
    this.messageTimeout = options.messageTimeout ?? DEFAULT_MESSAGE_TIMEOUT;
    this.authOrigin = options.authOrigin ?? '';
    this.unauthorizedRedirectUrl = options.unauthorizedRedirectUrl ?? DEFAULT_UNAUTHORIZED_REDIRECT_URL;
  }

  async connect(): Promise<WebSocket> {
    try {
      return await this.openSocket();
    } catch {
      return await this.retryWithRefresh();
    }
  }

  async sendAndReceive<T>(payload: Record<string, unknown>, options?: { timeout?: number }): Promise<T> {
    if (!this.url) {
      throw new WebSocketError('WebSocket URL not configured', 'CONNECT_FAILED', 0);
    }

    const ws = await this.connect();
    const timeout = options?.timeout ?? this.connectTimeout;

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        ws.close();
        reject(new WebSocketError('タイムアウトしました', 'TIMEOUT', 0));
      }, timeout);

      ws.send(JSON.stringify(payload));

      ws.onmessage = (e) => {
        try {
          const raw = JSON.parse(e.data as string) as { event: string; data: unknown };
          clearTimeout(timer);
          ws.close();
          if (raw.event === 'error') {
            const err = raw.data as { message: string };
            reject(new WebSocketError(err.message, 'SERVER_ERROR', 0));
          } else {
            resolve(raw.data as T);
          }
        } catch {
          // ignore malformed messages
        }
      };

      ws.onerror = () => {
        clearTimeout(timer);
        reject(new WebSocketError('ネットワークに接続できません', 'NETWORK_ERROR', 0));
      };

      ws.onclose = (e) => {
        clearTimeout(timer);
        if (e.code !== 1000 && e.code !== 1005) {
          reject(new WebSocketError('接続が切断されました', 'DISCONNECTED', 0));
        }
      };
    });
  }

  async sendAndStream(
    payload: Record<string, unknown>,
    onMessage: (raw: { event: string; data: Record<string, unknown> }) => void,
    options?: { timeout?: number; signal?: AbortSignal; isTerminal?: (event: string) => boolean },
  ): Promise<void> {
    if (!this.url) {
      throw new WebSocketError('WebSocket URL not configured', 'CONNECT_FAILED', 0);
    }

    const ws = await this.connect();
    const timeout = options?.timeout ?? this.messageTimeout;
    const isTerminal = options?.isTerminal ?? ((event: string) => event === 'run_end');

    return new Promise((resolve, reject) => {
      let settled = false;
      const settle = (fn: () => void) => {
        if (!settled) {
          settled = true;
          fn();
        }
      };

      const timer = setTimeout(() => {
        ws.close();
        settle(() =>
          reject(
            new WebSocketError(
              '応答に時間がかかっています。再試行するか、後でもう一度お試しください。',
              'TIMEOUT',
              0,
            ),
          ),
        );
      }, timeout);

      if (options?.signal) {
        options.signal.addEventListener('abort', () => {
          clearTimeout(timer);
          ws.close();
          settle(() => resolve());
        });
      }

      ws.send(JSON.stringify(payload));

      ws.onmessage = (e) => {
        try {
          const raw = JSON.parse(e.data as string) as {
            event: string;
            data: Record<string, unknown>;
          };
          onMessage(raw);

          if (raw.event === 'error') {
            clearTimeout(timer);
            ws.close();
            const message = (raw.data.message as string) || '';
            const code = raw.data.code as string | undefined;
            const statusCode = code === 'AUTH_ERROR' ? 401 : code === 'TOKEN_NOT_FOUND' ? 404 : 503;
            settle(() => reject(new WebSocketError(message, 'SERVER_ERROR', statusCode)));
            return;
          }

          if (isTerminal(raw.event)) {
            clearTimeout(timer);
            ws.close();
            settle(() => resolve());
          }
        } catch {
          // ignore malformed messages
        }
      };

      ws.onerror = () => {
        clearTimeout(timer);
        settle(() =>
          reject(
            new WebSocketError(
              'ネットワークに接続できません。インターネット接続を確認してください。',
              'NETWORK_ERROR',
              0,
            ),
          ),
        );
      };

      ws.onclose = (e) => {
        clearTimeout(timer);
        if (e.code === 1000 || e.code === 1005) {
          settle(() => resolve());
        } else {
          settle(() =>
            reject(new WebSocketError('接続が切断されました。再試行してください。', 'DISCONNECTED', 0)),
          );
        }
      };
    });
  }

  private openSocket(): Promise<WebSocket> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(this.url);

      const timer = setTimeout(() => {
        cleanup();
        ws.close();
        reject(new WebSocketError('接続がタイムアウトしました', 'TIMEOUT', 0));
      }, this.connectTimeout);

      const onOpen = () => {
        cleanup();
        resolve(ws);
      };
      const onError = () => {
        cleanup();
        reject(new WebSocketError('WebSocket接続に失敗しました', 'CONNECT_FAILED', 0));
      };
      const onClose = (e: CloseEvent) => {
        cleanup();
        if (e.code === 1000 || e.code === 1005) resolve(ws);
        else reject(new WebSocketError(`ws_close_${e.code}`, 'CONNECT_FAILED', 0));
      };
      const cleanup = () => {
        clearTimeout(timer);
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onError);
        ws.removeEventListener('close', onClose);
      };

      ws.addEventListener('open', onOpen);
      ws.addEventListener('error', onError);
      ws.addEventListener('close', onClose);
    });
  }

  private async retryWithRefresh(): Promise<WebSocket> {
    try {
      const { RestClient } = await import('@ps-smart-cp/ppfw-client');
      const authClient = new RestClient(this.authOrigin);
      await authClient.refreshToken();
      return await this.openSocket();
    } catch {
      window.location.assign(this.unauthorizedRedirectUrl);
      throw new WebSocketError('認証に失敗しました。ログイン画面に遷移します。', 'AUTH_FAILED', 401);
    }
  }
}
